import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { defaultFonts } from "@event-planner/shared";

interface EmailPreviewProps {
  eventName: string;
  title: string;
  body: string;
  deepLink: string | null;
}

const GRADIENT_START = "#21376C";
const GRADIENT_END = "#3B5398";

/**
 * Live email preview rendered as React components — no iframe, no flicker.
 * Uses the same fonts already loaded by the admin app (@fontsource imports
 * in main.tsx), matching the branded gradient header from the real email
 * template (buildEmailShell).
 */
export function EmailPreview({ eventName, title, body, deepLink }: EmailPreviewProps) {
  const paragraphs = (body || "Your message will appear here.")
    .split(/\n{2,}/);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Email Preview
      </Typography>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          bgcolor: "#f5f5f5",
        }}
      >
        {/* Outer padding mimicking email client chrome */}
        <Box sx={{ p: 2 }}>
          {/* Email card */}
          <Box
            sx={{
              maxWidth: 520,
              mx: "auto",
              bgcolor: "#ffffff",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {/* Gradient header */}
            <Box
              sx={{
                background: `linear-gradient(to right, ${GRADIENT_START}, ${GRADIENT_END})`,
                px: 4,
                py: 4,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: defaultFonts.admin.display,
                  fontSize: 28,
                  fontWeight: 400,
                  color: "#ffffff",
                  letterSpacing: 0,
                  lineHeight: 1.2,
                }}
              >
                {eventName || "Event Name"}
              </Typography>
            </Box>

            {/* Body */}
            <Box sx={{ px: 4, py: 4 }}>
              <Typography
                sx={{
                  fontFamily: defaultFonts.admin.body,
                  fontSize: 18,
                  fontWeight: 600,
                  color: GRADIENT_START,
                  mb: 2,
                }}
              >
                {title || "Notification title"}
              </Typography>

              {paragraphs.map((p, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontFamily: defaultFonts.admin.body,
                    fontSize: 15,
                    color: "#333",
                    lineHeight: 1.6,
                    mb: 2,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {p}
                </Typography>
              ))}

              {deepLink && (
                <Box sx={{ textAlign: "center", mt: 3, mb: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      bgcolor: GRADIENT_START,
                      color: "#ffffff",
                      fontFamily: defaultFonts.admin.body,
                      fontSize: 14,
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 1.5,
                    }}
                  >
                    Open in App
                  </Box>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 4,
                py: 2,
                bgcolor: "#fafafa",
                borderTop: "1px solid #eee",
              }}
            >
              <Typography
                sx={{
                  fontFamily: defaultFonts.admin.body,
                  fontSize: 12,
                  color: "#999",
                  textAlign: "center",
                }}
              >
                This is an automated message. If you were not expecting this
                email, you can ignore it.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, textAlign: "center" }}
      >
        Email as recipients will see it
      </Typography>
    </Box>
  );
}
